import { Component, ElementRef, inject, signal, HostListener, computed, Output, EventEmitter, OnInit, OnDestroy, effect, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AuthService } from '../../../services/auth.service';
import { InstitutionContextService } from '../../../services/institution-context.service';
import { ScholarshipApiService } from '../../../services/api/scholarship-api.service';
import { Institution } from '../../../models/scholarship.model';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './header.html',
  styleUrls: ['./header.scss'],
})
export class HeaderComponent implements OnInit, OnDestroy {
  private readonly unreadMessagesPollingMs = 60_000;
  private readonly auth = inject(AuthService);
  private readonly institutionContext = inject(InstitutionContextService);
  private readonly api = inject(ScholarshipApiService);
  private readonly elementRef = inject(ElementRef);
  private unreadMessagesPollingId: ReturnType<typeof setInterval> | null = null;

  readonly user = this.auth.user;
  readonly institutions = this.institutionContext.institutions;
  readonly selectedInstitutionId = this.institutionContext.selectedInstitutionId;
  readonly dropdownOpen = signal(false);
  readonly institutionDropdownOpen = signal(false);
  readonly unreadMessages = signal(0);
  readonly unreadMessagesLoading = signal(false);

  @Input() sidebarOpen = false;
  @Output() toggleSidebar = new EventEmitter<void>();

  /** Iniciais do usuário para o avatar */
  readonly initials = computed(() => {
    const u = this.user();
    if (!u) return '?';
    if (u.name) {
      const parts = u.name.trim().split(/\s+/);
      return parts.length >= 2
        ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
        : parts[0].slice(0, 2).toUpperCase();
    }
    return u.email.slice(0, 2).toUpperCase();
  });

  readonly unreadMessagesLabel = computed(() => {
    const count = this.unreadMessages();
    if (count <= 0) return 'Nenhuma mensagem não lida';
    if (count === 1) return '1 mensagem não lida';
    return `${count} mensagens não lidas`;
  });

  readonly unreadMessagesBadge = computed(() => {
    const count = this.unreadMessages();
    return count > 99 ? '99+' : String(count);
  });

  readonly selectedInstitution = computed(() => {
    const selectedId = this.selectedInstitutionId();
    return this.institutions().find((institution) => institution.id === selectedId) ?? null;
  });

  readonly selectedInstitutionName = computed(() => {
    return this.selectedInstitution()?.name ?? 'Selecionar instituição';
  });

  constructor() {
    effect(() => {
      const institutionId = this.selectedInstitutionId();
      if (institutionId > 0) {
        setTimeout(() => this.loadUnreadMessages(institutionId), 0);
      }
    });
  }

  ngOnInit(): void {
    this.institutionContext.loadActiveInstitutions();
    this.startUnreadMessagesPolling();
  }

  ngOnDestroy(): void {
    this.stopUnreadMessagesPolling();
  }

  changeInstitution(value: string | number): void {
    this.institutionContext.setSelectedInstitutionId(Number(value));
    this.institutionDropdownOpen.set(false);
  }

  refreshUnreadMessages(): void {
    this.loadUnreadMessages(this.selectedInstitutionId(), true);
  }

  /** Primeiro nome do usuário para exibição no trigger */
  readonly firstName = computed(() => {
    const u = this.user();
    if (!u) return 'Usuário';
    return u.name ? u.name.trim().split(/\s+/)[0] : u.email;
  });

  logout(): void {
    this.dropdownOpen.set(false);
    this.institutionDropdownOpen.set(false);
    this.auth.logout();
  }

  toggleDropdown(): void {
    this.institutionDropdownOpen.set(false);
    this.dropdownOpen.update((v) => !v);
  }

  toggleInstitutionDropdown(): void {
    this.dropdownOpen.set(false);
    this.institutionDropdownOpen.update((v) => !v);
  }

  selectInstitution(institution: Institution): void {
    this.changeInstitution(institution.id);
  }

  isSelectedInstitution(institution: Institution): boolean {
    return institution.id === this.selectedInstitutionId();
  }

  private loadUnreadMessages(institutionId: number, force = false): void {
    if (!institutionId) return;
    if (this.unreadMessagesLoading() && !force) return;
    this.unreadMessagesLoading.set(true);
    this.api.getUnreadCount(institutionId).subscribe({
      next: (count) => {
        if (this.selectedInstitutionId() === institutionId) {
          this.unreadMessages.set(count ?? 0);
        }
        this.unreadMessagesLoading.set(false);
      },
      error: () => {
        if (this.selectedInstitutionId() === institutionId) {
          this.unreadMessages.set(0);
        }
        this.unreadMessagesLoading.set(false);
      },
    });
  }

  private startUnreadMessagesPolling(): void {
    this.stopUnreadMessagesPolling();
    this.unreadMessagesPollingId = setInterval(() => {
      this.loadUnreadMessages(this.selectedInstitutionId());
    }, this.unreadMessagesPollingMs);
  }

  private stopUnreadMessagesPolling(): void {
    if (this.unreadMessagesPollingId) {
      clearInterval(this.unreadMessagesPollingId);
      this.unreadMessagesPollingId = null;
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const el = this.elementRef.nativeElement as HTMLElement;
    if (!el.contains(event.target as Node)) {
      this.dropdownOpen.set(false);
      this.institutionDropdownOpen.set(false);
    }
  }

  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    this.dropdownOpen.set(false);
    this.institutionDropdownOpen.set(false);
  }
}
